import { useCallback } from 'react';
import { createProductKey } from '../AppUtils.js';
import { isWheelRimCategory, allowsMultipleSelection } from '../constants.js';

export const useProductHandlers = ({
  toggleProduct,
  removeProduct,
  addProductWithRequirements,
  switchBrandEcosystem,
  switchDisplayEcosystem,
  sortProductsByCompatibility,
  selectedProducts,
  productData,
  setRequirementData,
  setShowRequirementPopup,
  setBrandConflictData,
  setShowBrandConflictPopup,
  setDisplayConflictData,
  setShowDisplayConflictPopup,
  setSizeConflictData,
  setShowSizeConflictPopup,
  setLockedMessage,
  setShowLockedPopup,
  setSelectedMainCategory,
  setViewState,
  setSelectedSubCategory,
  setShowMobileBuildSummary,
  setSelectedProduct,
  categoryConfig,
  // Compulsory handling
  handleCompulsoryRemoval,
  handleProductAdded,
  pendingCompulsory,
  isSubcategoryCompulsory,
  isCategoryCompulsory,
  onTriggerCompulsoryShake
}) => {
  const handleProductToggle = useCallback((product, mainCategory, subCategory) => {
    // Check if this product is currently in build (to detect add vs remove)
    const isWheelRim = isWheelRimCategory(subCategory);
    const allowsMultiple = isWheelRim ||
                           allowsMultipleSelection(subCategory, categoryConfig) ||
                           allowsMultipleSelection(mainCategory, categoryConfig);
    
    let wasInBuild = false;
    let productKey = '';
    if (allowsMultiple) {
      productKey = `${createProductKey(mainCategory, subCategory)}_${product.id}`;
      wasInBuild = !!selectedProducts[productKey];
    } else {
      productKey = createProductKey(mainCategory, subCategory);
      wasInBuild = selectedProducts[productKey]?.id === product.id;
    }

    // If this is a REMOVAL, check compulsory BEFORE removing
    if (wasInBuild && handleCompulsoryRemoval) {
      // Call to set pending state - navigation blocking happens via shouldBlockNavigation
      handleCompulsoryRemoval(productKey, product);
    }

    const result = toggleProduct(product, mainCategory, subCategory);

    if (result.success) {
      // If this was an ADD (not remove), notify compulsory handler
      if (!wasInBuild && handleProductAdded) {
        handleProductAdded(mainCategory, subCategory);
      }
    } else if (result.requirementData) {
      setRequirementData(result.requirementData);
      setShowRequirementPopup(true);
    } else if (result.brandConflictData) {
      setBrandConflictData(result.brandConflictData);
      setShowBrandConflictPopup(true);
    } else if (result.displayConflictData) {
      setDisplayConflictData(result.displayConflictData);
      setShowDisplayConflictPopup(true);
    } else if (result.sizeConflictData) {
      setSizeConflictData(result.sizeConflictData);
      setShowSizeConflictPopup(true);
    } else if (result.lockedProduct) {
      setLockedMessage(result.message);
      setShowLockedPopup(true);
    }
  }, [toggleProduct, selectedProducts, categoryConfig, handleProductAdded, handleCompulsoryRemoval, setRequirementData, setShowRequirementPopup, setBrandConflictData, setShowBrandConflictPopup, setDisplayConflictData, setShowDisplayConflictPopup, setSizeConflictData, setShowSizeConflictPopup, setLockedMessage, setShowLockedPopup]);

  const handleRemoveProduct = useCallback((productKey) => {
    // Get the product before removal for compulsory check
    const product = selectedProducts[productKey];
    
    if (!product) {
      return;
    }

    // Parse the product key to get category info
    const baseKey = productKey.split('_')[0];
    const keyParts = baseKey.split('||');
    const productMainCategory = keyParts[0];
    const productSubCategory = keyParts[1];

    // BLOCK: If there's already a pending compulsory requirement,
    // don't allow removing OTHER compulsory items (trigger shake instead)
    if (pendingCompulsory) {
      const isThisCompulsory = (isSubcategoryCompulsory && isSubcategoryCompulsory(productSubCategory)) ||
                               (isCategoryCompulsory && isCategoryCompulsory(productMainCategory));
      
      if (isThisCompulsory) {
        // Trigger shake animation on the banner
        if (onTriggerCompulsoryShake) {
          onTriggerCompulsoryShake();
        }
        return;
      }
    }
    
    // Check if this is a compulsory category removal
    let compulsoryResult = { shouldBlock: false, navigateTo: null };
    if (handleCompulsoryRemoval && product) {
      compulsoryResult = handleCompulsoryRemoval(productKey, product);
    }

    const result = removeProduct(productKey);

    if (!result.success && result.message) {
      setLockedMessage(result.message);
      setShowLockedPopup(true);
      return;
    }

    // If compulsory item was removed and we need to navigate
    if (compulsoryResult.navigateTo) {
      const { mainCategory, subCategory } = compulsoryResult.navigateTo;
      
      // Close mobile summary if open
      setShowMobileBuildSummary(false);
      
      // Navigate to the compulsory category
      setSelectedMainCategory(mainCategory);
      setViewState('sub');
      
      if (subCategory) {
        setSelectedSubCategory(subCategory);
      } else if (categoryConfig[mainCategory]?.subcategoryOrder?.length > 0) {
        // Set first subcategory
        setSelectedSubCategory(categoryConfig[mainCategory].subcategoryOrder[0]);
      }
      
      setSelectedProduct(0);
    }
  }, [removeProduct, selectedProducts, handleCompulsoryRemoval, pendingCompulsory, isSubcategoryCompulsory, isCategoryCompulsory, onTriggerCompulsoryShake, setLockedMessage, setShowLockedPopup, setShowMobileBuildSummary, setSelectedMainCategory, setViewState, setSelectedSubCategory, setSelectedProduct, categoryConfig]);

  const handleAddProductWithRequirements = useCallback((originalProduct, missingProducts, selectedMainCategory, selectedSubCategory) => {
    addProductWithRequirements(originalProduct, missingProducts, selectedMainCategory, selectedSubCategory);
    setShowRequirementPopup(false);
    setRequirementData(null);
    
    // Notify compulsory handler that product was added
    if (handleProductAdded) {
      handleProductAdded(selectedMainCategory, selectedSubCategory);
    }
  }, [addProductWithRequirements, handleProductAdded, setShowRequirementPopup, setRequirementData]);

  // Define handleNavigateToCategory FIRST (before handleSwitchEcosystem)
  // NOW WITH COMPULSORY BLOCKING
  const handleNavigateToCategory = useCallback((mainCategory, subCategory = '', targetProduct = null) => {
    // CHECK: Block navigation if there's a pending compulsory requirement
    // UNLESS we're navigating TO the compulsory category/subcategory
    if (pendingCompulsory) {
      const isNavigatingToCompulsory = 
        (pendingCompulsory.type === 'category' && mainCategory === pendingCompulsory.name) ||
        (pendingCompulsory.type === 'subcategory' && subCategory === pendingCompulsory.name) ||
        (pendingCompulsory.type === 'subcategory' && !subCategory && pendingCompulsory.mainCategory === mainCategory);
      
      if (!isNavigatingToCompulsory) {
        // Trigger shake animation
        if (onTriggerCompulsoryShake) {
          onTriggerCompulsoryShake();
        }
        return; // Block navigation
      }
    }
    
    setSelectedMainCategory(mainCategory);
    setViewState('sub');
    setShowMobileBuildSummary(false);

    if (subCategory) {
      setSelectedSubCategory(subCategory);
    } else {
      if (categoryConfig[mainCategory]?.subcategoryOrder && categoryConfig[mainCategory].subcategoryOrder.length > 0) {
        const firstSubCategory = categoryConfig[mainCategory].subcategoryOrder[0];
        setSelectedSubCategory(firstSubCategory);
        subCategory = firstSubCategory;
      } else {
        const actualSubCategories = Object.keys(productData[mainCategory] || {}).sort();
        if (actualSubCategories.length === 1) {
          setSelectedSubCategory(actualSubCategories[0]);
          subCategory = actualSubCategories[0];
        } else if (actualSubCategories.length > 1) {
          setSelectedSubCategory(actualSubCategories[0]);
          subCategory = actualSubCategories[0];
        } else {
          setSelectedSubCategory('');
        }
      }
    }

    if (targetProduct && subCategory) {
      setTimeout(() => {
        const products = productData[mainCategory]?.[subCategory] || [];
        const sortedProducts = sortProductsByCompatibility(products, selectedProducts);
        const productIndex = sortedProducts.findIndex(p => p.id === targetProduct.id);
        if (productIndex !== -1) {
          setSelectedProduct(productIndex);
        } else {
          setSelectedProduct(0);
        }
      }, 10);
    } else {
      setSelectedProduct(0);
    }
  }, [setSelectedMainCategory, setViewState, setShowMobileBuildSummary, setSelectedSubCategory, categoryConfig, productData, sortProductsByCompatibility, selectedProducts, setSelectedProduct, pendingCompulsory, onTriggerCompulsoryShake]);

  // Now define handleSwitchEcosystem AFTER handleNavigateToCategory
  const handleSwitchEcosystem = useCallback((newProduct, conflictingProducts, targetSubCategory) => {
    // Close the brand conflict popup first
    setShowBrandConflictPopup(false);
    setBrandConflictData(null);

    // CRITICAL: switchBrandEcosystem removes ALL brand-dependent products (Wheel Base AND Wheel Rim)
    // We need to check if ANY of these are from compulsory categories BEFORE removing
    // BUT we should NOT trigger compulsory check for the subcategory that the new product will satisfy
    const brandDependentSubcategories = ['Wheel Base', 'Wheel Rim'];
    let compulsorySubcategoryNeeded = null;
    
    if (handleCompulsoryRemoval) {
      // Find ALL brand-dependent products in current selection that will be removed
      // and are NOT the same subcategory as the new product
      Object.entries(selectedProducts).forEach(([key, product]) => {
        if (brandDependentSubcategories.includes(product.subCategory)) {
          // SKIP if this is the same subcategory as what we're adding
          // (because the new product will satisfy this requirement)
          if (product.subCategory === newProduct.subCategory) {
            return;
          }
          
          // Check if this OTHER subcategory is compulsory
          const productKey = createProductKey(product.mainCategory || 'Racing Controls', product.subCategory);
          const result = handleCompulsoryRemoval(productKey, product);

          if (result.navigateTo) {
            compulsorySubcategoryNeeded = result.navigateTo.subCategory;
          }
        }
      });
    }

    // Switch the ecosystem (removes old products, adds new one)
    switchBrandEcosystem(newProduct);

    // After switching, the new product satisfies its own subcategory
    // So we need to notify the compulsory handler that we added a product
    if (handleProductAdded) {
      handleProductAdded(newProduct.mainCategory || 'Racing Controls', newProduct.subCategory);
    }

    // Use requestAnimationFrame + setTimeout to ensure state has fully updated
    // before navigating - this gives React time to process the selectedProducts update
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Force navigation even if already on the same category
        // This ensures products are re-sorted with new ecosystem
        setSelectedProduct(0);  // Reset to first product
        
        // If a compulsory item from a DIFFERENT subcategory was removed,
        // navigate to that subcategory so user can select a replacement
        if (compulsorySubcategoryNeeded) {
          handleNavigateToCategory('Racing Controls', compulsorySubcategoryNeeded);
        } else {
          handleNavigateToCategory('Racing Controls', targetSubCategory);
        }
      }, 50);
    });
  }, [switchBrandEcosystem, handleNavigateToCategory, setShowBrandConflictPopup, setBrandConflictData, setSelectedProduct, handleCompulsoryRemoval, handleProductAdded, selectedProducts]);

  const handleSwitchDisplayEcosystem = useCallback((newProduct, newDisplayType) => {
    // Check if any display products being removed are from compulsory categories
    // BUT only for subcategories that WON'T be satisfied by the new product
    let compulsorySubcategoryNeeded = null;
    
    if (handleCompulsoryRemoval) {
      // Find ALL display products that will be removed (those with different display type)
      Object.entries(selectedProducts).forEach(([key, product]) => {
        // Get display type of existing product
        const existingDisplayType = product.displayTypeCompatibility?.[0] || 
          (product.name?.toLowerCase().includes('single') ? 'Single' :
           product.name?.toLowerCase().includes('triple') ? 'Triple' :
           product.name?.toLowerCase().includes('vr') ? 'VR' : null);
        
        // If this product has a display type and it's different from the new type, it will be removed
        if (existingDisplayType && existingDisplayType !== newDisplayType) {
          // SKIP if same subcategory as the new product (will be replaced)
          if (product.subCategory === newProduct.subCategory) {
            return;
          }
          
          const productKey = createProductKey(product.mainCategory, product.subCategory);
          const result = handleCompulsoryRemoval(productKey, product);

          if (result.navigateTo) {
            compulsorySubcategoryNeeded = result.navigateTo.subCategory;
          }
        }
      });
    }

    switchDisplayEcosystem(newProduct, newDisplayType);

    // Notify that we added a product (satisfies the new product's subcategory)
    if (handleProductAdded) {
      handleProductAdded(newProduct.mainCategory, newProduct.subCategory);
    }

    setShowDisplayConflictPopup(false);
    setDisplayConflictData(null);

    // If a compulsory item from a DIFFERENT subcategory was removed, navigate there
    if (compulsorySubcategoryNeeded) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          handleNavigateToCategory(newProduct.mainCategory, compulsorySubcategoryNeeded);
        }, 50);
      });
    }
  }, [switchDisplayEcosystem, setShowDisplayConflictPopup, setDisplayConflictData, handleCompulsoryRemoval, handleProductAdded, selectedProducts, handleNavigateToCategory]);

  const isProductInBuild = useCallback((product, selectedMainCategory, selectedSubCategory) => {
    const isWheelRim = isWheelRimCategory(selectedSubCategory);
    const allowsMultiple = isWheelRim ||
                           allowsMultipleSelection(selectedSubCategory, categoryConfig) ||
                           allowsMultipleSelection(selectedMainCategory, categoryConfig);

    if (allowsMultiple) {
      const uniqueKey = `${createProductKey(selectedMainCategory, selectedSubCategory)}_${product.id}`;
      return !!selectedProducts[uniqueKey];
    } else {
      const categoryKey = createProductKey(selectedMainCategory, selectedSubCategory);
      const productInCurrentCategory = selectedProducts[categoryKey];

      if (productInCurrentCategory?.id === product.id) {
        return true;
      }

      if (selectedMainCategory === 'Display' || selectedMainCategory === 'Display & Audio' || selectedMainCategory === 'Displays') {
        return Object.values(selectedProducts).some(selectedProduct =>
          selectedProduct.id === product.id &&
          selectedProduct.slug === product.slug &&
          (selectedProduct.mainCategory === 'Display' ||
            selectedProduct.mainCategory === 'Display & Audio' ||
            selectedProduct.mainCategory === 'Displays')
        );
      }

      return false;
    }
  }, [selectedProducts, categoryConfig]);

  return {
    handleProductToggle,
    handleRemoveProduct,
    handleAddProductWithRequirements,
    handleSwitchEcosystem,
    handleSwitchDisplayEcosystem,
    handleNavigateToCategory,
    isProductInBuild
  };
};